/*
  mo:mo Reviews — echte judge.me-Bewertungen in unserem Card-Design.

  Warum so: judge.me rendert seine Widgets ausschließlich client-seitig (App-Extension
  `loader.js`). In Liquid liegen die Review-Texte deshalb nicht vor — nur Schnitt und Anzahl
  kommen serverseitig über die reviews-Metafelder (siehe snippets/momo-rating.liquid).

  Ablauf: Die Section rendert ein verstecktes judge.me-Widget. Sobald dessen Markup auftaucht,
  lesen wir die einzelnen `.jdgm-rev`-Knoten aus, filtern/mischen sie und bauen daraus unsere
  `.momo-reviews__card`-Struktur. Kommt nichts an (kein Widget, keine Bewertungen, JS blockiert),
  greift das im Schema gewählte Fallback: Section ausblenden oder manuelle Bewertungen stehen lassen.

  ACHTUNG: Wir hängen an fremdem DOM. Alle Selektoren sind defensiv (mehrere Varianten, überall
  Null-Checks) und Inhalte werden ausschließlich per textContent gesetzt — nie per innerHTML,
  weil Review-Texte Nutzereingaben sind.
*/
class MomoJudgemeReviews extends HTMLElement {
  connectedCallback() {
    this.grid = this.querySelector('[data-reviews-grid]');
    this.source = this.querySelector('[data-jdgm-source]');
    this.actions = this.querySelector('[data-reviews-actions]');
    this.moreWrap = this.querySelector('[data-reviews-more]');
    this.lessWrap = this.querySelector('[data-reviews-less]');
    if (!this.grid || !this.source) return;

    this.opts = {
      count: parseInt(this.dataset.count, 10) || 3,
      minRating: parseInt(this.dataset.minRating, 10) || 0,
      pick: this.dataset.pick || 'random',
      truncate: parseInt(this.dataset.truncate, 10) || 0,
      fallback: this.dataset.fallback || 'hide',
      avatar: this.dataset.avatar || 'initials',
      verifiedLabel: this.dataset.verifiedLabel || '',
      showDate: this.dataset.showDate === 'true',
      showProduct: this.dataset.showProduct === 'true',
      avatars: (this.dataset.avatars || '').split(',').filter(Boolean),
      step: parseInt(this.dataset.step, 10) || 9,
      timeout: parseInt(this.dataset.timeout, 10) || 9000,
    };

    this.pool = [];
    this.shown = this.opts.count;
    this.settled = false;
    this.observer = new MutationObserver(() => this.attempt());
    this.observer.observe(this.source, { childList: true, subtree: true });
    this.timer = setTimeout(() => this.giveUp(), this.opts.timeout);

    this.attempt();
  }

  disconnectedCallback() {
    this.stop();
  }

  stop() {
    if (this.observer) this.observer.disconnect();
    if (this.timer) clearTimeout(this.timer);
  }

  attempt() {
    if (this.settled || this.settling) return;
    if (!this.collect().length) return;

    /* Kurz warten: judge.me hängt seine Reviews nacheinander ein. Ohne diese Pause
       würfeln wir nur aus den ersten Treffern statt aus dem vollen Satz. */
    this.settling = true;
    setTimeout(() => {
      this.pool = this.select(this.collect());
      this.settled = true;
      this.stop();
      if (this.pool.length) {
        this.renderPage();
        this.bindPager();
      } else {
        this.settled = false;
        this.settling = false;
        this.giveUp();
      }
    }, 250);
  }

  /* Alle Review-Knoten des versteckten Widgets einlesen.

     judge.me liefert je nach Widget zwei verschiedene Strukturen:
       Review-/All-Reviews-Widget  .jdgm-rev      → .jdgm-rev__body / __author / __timestamp / [data-score]
       Testimonials-Carousel       .jdgm-card     → .jdgm-text / .jdgm-reviewer-name / .jdgm-stars (kein Datum)
     Beide werden hier auf dasselbe Objekt normalisiert. */
  collect() {
    const nodes = this.source.querySelectorAll('.jdgm-rev, .jdgm-card.jdgm-testimonial, .jdgm-testimonial');
    const out = [];
    const seen = {};

    nodes.forEach((node) => {
      const id = node.getAttribute('data-review-id') || '';
      if (id && seen[id]) return;
      if (id) seen[id] = true;

      const body = this.textOf(node, '.jdgm-rev__body') || this.textOf(node, '.jdgm-text');
      if (!body) return;

      out.push({
        rating: this.ratingOf(node),
        body: body,
        author: this.textOf(node, '.jdgm-rev__author') || this.textOf(node, '.jdgm-reviewer-name'),
        product: this.textOf(node, '.jdgm-rev__prod-link') || this.textOf(node, '.jdgm-product-name'),
        date: this.dateOf(node),
        verified:
          node.getAttribute('data-verified-buyer') === 'true' ||
          !!node.querySelector('.jdgm-rev__buyer-badge, .jdgm-rev__transparency-badge, .jdgm-reviewer-verified-icon'),
      });
    });

    return out;
  }

  ratingOf(node) {
    const el = node.querySelector('[data-score]');
    const score = el ? parseInt(el.getAttribute('data-score'), 10) : NaN;
    if (score > 0) return score;
    /* Fallback: gefüllte Sterne zählen, falls judge.me das Attribut mal weglässt. */
    /* 0 heißt „nicht auslesbar" — die Bewertung fliegt dann über den Mindest-Sterne-Filter
       raus. Bewusst kein Default auf 5, sonst erfinden wir eine Bewertung. */
    const on = node.querySelectorAll('.jdgm-star.jdgm--on, .jdgm-star.jdgm--star-on');
    return on.length || 0;
  }

  dateOf(node) {
    const el = node.querySelector('.jdgm-rev__timestamp');
    if (!el) return '';
    return el.getAttribute('data-content') || el.getAttribute('datetime') || el.textContent.trim();
  }

  textOf(node, selector) {
    const el = node.querySelector(selector);
    if (!el) return '';
    return el.textContent.replace(/\s+/g, ' ').trim();
  }

  /* Filtern, sortieren/mischen, auf die gewünschte Anzahl kürzen. */
  select(reviews) {
    /* Keine Mindestlänge: echte Bewertungen sind oft kurz („Tolles Produkt"). */
    let list = reviews.filter((r) => r.rating >= this.opts.minRating && r.body.length > 2);

    if (this.opts.pick === 'random') {
      for (let i = list.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        const tmp = list[i];
        list[i] = list[j];
        list[j] = tmp;
      }
    } else if (this.opts.pick === 'top') {
      list = list.slice().sort((a, b) => b.rating - a.rating);
    }
    /* 'recent' = Reihenfolge des Widgets (dort in judge.me sortierbar) */

    /* Volle Liste — das Kürzen auf die Kartenzahl passiert erst beim Rendern,
       damit „Alle Bewertungen" den Rest nachlegen kann. */
    return list;
  }

  render(reviews) {
    const frag = document.createDocumentFragment();
    reviews.forEach((r) => frag.appendChild(this.card(r)));

    this.grid.textContent = '';
    this.grid.appendChild(frag);
    this.grid.removeAttribute('aria-busy');
    this.setAttribute('data-jdgm-state', 'ready');
  }

  renderPage() {
    this.render(this.pool.slice(0, this.shown));
    this.syncPager();
  }

  /* Gestaffeltes Nachladen statt „alles auf einmal": Start bei der Kartenzahl, dann in
     Schritten (3 → 9 → 18 → 27 …). „Weniger anzeigen" geht denselben Weg zurück.
     Der Pool ist bereits vollständig geparst — wir rendern nur einen größeren Ausschnitt,
     es wird also nichts nachgeladen, nur das DOM wächst kontrolliert. */
  bindPager() {
    if (!this.actions) return;
    if (this.pool.length <= this.opts.count) {
      this.actions.hidden = true;
      return;
    }

    this.actions.hidden = false;
    const more = this.moreWrap && this.moreWrap.querySelector('.btn');
    const less = this.lessWrap && this.lessWrap.querySelector('.btn');

    if (more) {
      more.addEventListener('click', () => {
        const next = this.shown < this.opts.step ? this.opts.step : this.shown + this.opts.step;
        this.shown = Math.min(next, this.pool.length);
        this.renderPage();
      });
    }

    if (less) {
      less.addEventListener('click', () => {
        const next = this.shown - this.opts.step;
        this.shown = next <= this.opts.count ? this.opts.count : next;
        this.renderPage();
        this.scrollIntoView({ block: 'nearest' });
      });
    }

    this.syncPager();
  }

  /* Jeder Button verschwindet an seinem Ende — kein toter Klick. */
  syncPager() {
    if (!this.actions) return;
    if (this.moreWrap) this.moreWrap.hidden = this.shown >= this.pool.length;
    if (this.lessWrap) this.lessWrap.hidden = this.shown <= this.opts.count;
  }

  card(review) {
    const card = this.el('div', 'momo-reviews__card');

    const stars = this.el('p', 'momo-reviews__stars');
    stars.textContent = this.starString(review.rating);
    stars.setAttribute('aria-label', review.rating + ' von 5 Sternen');
    card.appendChild(stars);

    const quote = this.el('p', 'momo-reviews__quote');
    quote.textContent = '„' + this.shorten(review.body) + '"';
    card.appendChild(quote);

    const author = this.el('div', 'momo-reviews__author');
    if (this.opts.avatar === 'image' && this.opts.avatars.length) {
      const avatar = this.el('div', 'momo-reviews__avatar');
      const img = document.createElement('img');
      /* Deterministisch aus dem Namen: dieselbe Person bekommt immer denselben Avatar. */
      img.src = this.opts.avatars[this.hashOf(review.author) % this.opts.avatars.length];
      img.alt = '';
      img.width = 56;
      img.height = 56;
      img.loading = 'lazy';
      avatar.appendChild(img);
      author.appendChild(avatar);
    } else if (this.opts.avatar === 'initials') {
      const avatar = this.el('div', 'momo-reviews__avatar momo-reviews__avatar--initials');
      avatar.setAttribute('aria-hidden', 'true');
      const initials = this.el('span', 'momo-reviews__initials');
      initials.textContent = this.initialsOf(review.author);
      avatar.appendChild(initials);
      author.appendChild(avatar);
    }

    const col = this.el('div', 'momo-reviews__namecol');
    if (review.author) {
      const name = this.el('p', 'momo-reviews__name');
      name.textContent = review.author;
      col.appendChild(name);
    }

    const meta = this.metaLine(review);
    if (meta) {
      const verified = this.el('p', 'momo-reviews__verified');
      verified.textContent = meta;
      col.appendChild(verified);
    }

    author.appendChild(col);
    card.appendChild(author);
    return card;
  }

  metaLine(review) {
    const parts = [];
    if (review.verified && this.opts.verifiedLabel) parts.push(this.opts.verifiedLabel);
    if (this.opts.showDate) {
      const formatted = this.formatDate(review.date);
      if (formatted) parts.push(formatted);
    }
    /* Nur sinnvoll bei shop-weiten Bewertungen — auf der eigenen PDS wäre es Redundanz. */
    if (this.opts.showProduct && review.product) parts.push(review.product);
    return parts.join(' · ');
  }

  formatDate(value) {
    if (!value) return '';
    const date = new Date(value);
    if (isNaN(date.getTime())) return '';
    const locale = document.documentElement.lang || 'de';
    try {
      return date.toLocaleDateString(locale, { month: 'long', year: 'numeric' });
    } catch (e) {
      return '';
    }
  }

  starString(rating) {
    let out = '';
    for (let i = 1; i <= 5; i++) out += i <= rating ? '★' : '☆';
    return out;
  }

  shorten(text) {
    const max = this.opts.truncate;
    if (!max || text.length <= max) return text;
    const cut = text.slice(0, max);
    const space = cut.lastIndexOf(' ');
    return (space > max * 0.6 ? cut.slice(0, space) : cut).replace(/[.,;:\s]+$/, '') + ' …';
  }

  hashOf(value) {
    let hash = 0;
    const text = value || '';
    for (let i = 0; i < text.length; i++) hash = (hash * 31 + text.charCodeAt(i)) % 100000;
    return hash;
  }

  initialsOf(name) {
    if (!name) return '';
    return name
      .split(/\s+/)
      .slice(0, 2)
      .map((part) => part.charAt(0).toUpperCase())
      .join('');
  }

  el(tag, className) {
    const node = document.createElement(tag);
    node.className = className;
    return node;
  }

  /* Nichts angekommen: je nach Setting manuelle Karten stehen lassen oder Section ausblenden. */
  giveUp() {
    if (this.settled) return;
    this.settled = true;
    this.stop();
    this.grid.removeAttribute('aria-busy');
    this.setAttribute('data-jdgm-state', 'empty');

    const inDesignMode = window.Shopify && window.Shopify.designMode;

    if (this.opts.fallback === 'manual') {
      this.grid.querySelectorAll('[data-skeleton]').forEach((node) => node.remove());
      return;
    }

    this.grid.querySelectorAll('[data-skeleton]').forEach((node) => node.remove());

    if (inDesignMode) {
      /* Im Editor lädt judge.me in der Regel nicht — Section nicht wegblenden, sonst wirkt sie kaputt. */
      const note = this.el('p', 'momo-reviews__editor-note');
      note.textContent =
        'Hier erscheinen die judge.me-Bewertungen. In der Editor-Vorschau werden sie nicht geladen — bitte in der Live-Vorschau prüfen.';
      this.grid.appendChild(note);
      return;
    }

    const section = this.closest('.momo-reviews');
    if (section) section.hidden = true;
  }
}

if (!customElements.get('momo-judgeme-reviews')) {
  customElements.define('momo-judgeme-reviews', MomoJudgemeReviews);
}

/*
  Rating-Zeile unter dem Produkttitel (★★★★★ 4,9 · 432 Bewertungen).

  Primärquelle sind die Shopify-Rating-Metafelder, serverseitig — siehe snippets/momo-rating.liquid.
  Dieses Element springt nur ein, wenn die leer sind: Es liest Schnitt und Anzahl aus einem
  versteckten judge.me-Preview-Badge desselben Produkts. Kommt nichts an, bleibt es unsichtbar —
  eine Zeile, die es nicht gibt, ist besser als eine erfundene.
*/
class MomoJudgemeRating extends HTMLElement {
  connectedCallback() {
    this.starsEl = this.querySelector('[data-rating-stars]');
    this.textEl = this.querySelector('[data-rating-text]');
    this.source = this.querySelector('[data-jdgm-source]');
    if (!this.starsEl || !this.textEl || !this.source) return;

    this.observer = new MutationObserver(() => this.attempt());
    this.observer.observe(this.source, { childList: true, subtree: true, attributes: true });
    this.timer = setTimeout(() => this.observer.disconnect(), 9000);
    this.attempt();
  }

  disconnectedCallback() {
    if (this.observer) this.observer.disconnect();
    if (this.timer) clearTimeout(this.timer);
  }

  attempt() {
    const node = this.source.querySelector('[data-number-of-reviews]');
    if (!node) return;

    const count = parseInt(node.getAttribute('data-number-of-reviews'), 10);
    const average = parseFloat(node.getAttribute('data-average-rating'));
    if (!(count > 0) || !(average > 0)) return;

    this.observer.disconnect();
    clearTimeout(this.timer);

    let stars = '';
    for (let i = 1; i <= 5; i++) stars += i <= Math.round(average) ? '★' : '☆';
    this.starsEl.textContent = stars;

    /* Pluralform kommt fertig aus den Locales; im Mehrzahl-Satz steckt eine Beispielzahl,
       die wir gegen die echte tauschen. */
    const template = count === 1 ? this.dataset.countOne : this.dataset.countOther;
    const label = (template || '').replace(/\d+/, this.formatCount(count));
    this.textEl.textContent = this.formatAverage(average) + (label ? ' · ' + label : '');

    this.hidden = false;
  }

  formatAverage(value) {
    const locale = document.documentElement.lang || 'de';
    try {
      return value.toLocaleString(locale, { minimumFractionDigits: 1, maximumFractionDigits: 1 });
    } catch (e) {
      return value.toFixed(1).replace('.', ',');
    }
  }

  formatCount(value) {
    const locale = document.documentElement.lang || 'de';
    try {
      return value.toLocaleString(locale);
    } catch (e) {
      return String(value);
    }
  }
}

if (!customElements.get('momo-judgeme-rating')) {
  customElements.define('momo-judgeme-rating', MomoJudgemeRating);
}
